import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showNotification } from "services/NotificationService";
import { FAX_DISPOSITION_PROGRAM_CODES } from "./FaxDispositionConfig";
import {
  buildSwitchDetailsNotificationState,
  SWITCH_DETAILS_NOTIFICATION_TYPES,
} from "./switchDetailsNotificationState";
import {
  fetchRawDetails,
  submitDisposition,
  submitRawForReview,
  unclaimRaw,
  discardRaw,
} from "./FaxDispositionService";

const INITIAL_FORM_VALUES = {
  disposition: "",
  targets: [],
  notes: "",
  rejectionReason: "",
  futureSwitchTreatment: "",
  dmrOptions: [],
};

const SWITCH_NOTIFICATIONS = {
  successTitle: "Successfully updated Switch Disposition!",
  successMessage: "The switch disposition has been updated for this switch.",
  errorMessage: "Unable to save switch disposition.",
};

const getInitialFormValues = (rawDetails, changeDisposition, faxProgramCode) => {
  if (changeDisposition) {
    return { ...INITIAL_FORM_VALUES };
  }

  const disposition = rawDetails?.disposition;
  const isDmrProgram = faxProgramCode === FAX_DISPOSITION_PROGRAM_CODES.DMR;

  if (isDmrProgram) {
    return {
      ...INITIAL_FORM_VALUES,
      dmrOptions: rawDetails?.dmrDetails?.dmrOptions ?? [],
      notes: disposition?.note?.note ?? "",
    };
  }

  if (disposition) {
    return {
      disposition: disposition.disposition || "",
      targets: disposition.selectedTargets || [],
      notes:
        disposition.note && disposition.disposition !== "DECLINED"
          ? disposition.note.note
          : "",
      rejectionReason: disposition.rejectionReason || "",
      futureSwitchTreatment: disposition.futureSwitchTreatment || "",
      dmrOptions: disposition.dmrOptions || [],
    };
  }

  return { ...INITIAL_FORM_VALUES };
};

const notifySwitchDispositionSuccess = () => {
  showNotification({
    title: SWITCH_NOTIFICATIONS.successTitle,
    message: SWITCH_NOTIFICATIONS.successMessage,
    position: "tr",
    type: "success",
  });
};

const notifySwitchDispositionError = (position = "tr") => {
  showNotification({
    title: "Error!",
    message: SWITCH_NOTIFICATIONS.errorMessage,
    position,
    type: "error",
  });
};

export const useFaxDisposition = ({
  rawId,
  faxProgramCode,
  changeDisposition = false,
  contextIsRaw = true,
  isSubmissionRequestFromSwim = false,
}) => {
  const navigate = useNavigate();
  const isSwitchProgram =
    faxProgramCode === FAX_DISPOSITION_PROGRAM_CODES.SWITCH;

  const [rawDetails, setRawDetails] = useState(null);
  const [rawDetailsStatus, setRawDetailsStatus] = useState("requested");
  const [formValues, setFormValues] = useState({ ...INITIAL_FORM_VALUES });
  const [submittingDisposition, setSubmittingDisposition] = useState(false);
  const [submittedDisposition, setSubmittedDisposition] = useState(false);
  const [submitDispositionFailed, setSubmitDispositionFailed] = useState(false);
  const [eventRequestCompleted, setEventRequestCompleted] = useState(false);
  const [isSubmissionRequestFromAw, setIsSubmissionRequestFromAw] =
    useState(false);

  const navigateToSwitchDetails = useCallback(
    (wfItemId, notificationType) => {
      navigate(`/switch/${wfItemId}`, {
        replace: true,
        state: buildSwitchDetailsNotificationState(notificationType),
      });
    },
    [navigate]
  );

  const loadRawDetails = useCallback(() => {
    setRawDetailsStatus("requested");
    fetchRawDetails(rawId)
      .then((data) => {
        setRawDetails(data);
        setFormValues(
          getInitialFormValues(data, changeDisposition, faxProgramCode)
        );
        setRawDetailsStatus("success");
      })
      .catch(() => {
        setRawDetails(null);
        setRawDetailsStatus("failure");
      });
  }, [rawId, changeDisposition, faxProgramCode]);

  useEffect(() => {
    loadRawDetails();
  }, [loadRawDetails]);

  const setFieldValue = useCallback((name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetDispositionFields = useCallback((dispositionValue) => {
    setFormValues((prev) => ({
      ...prev,
      disposition: dispositionValue,
      targets: [],
      rejectionReason: "",
      futureSwitchTreatment: "",
      notes: "",
    }));
  }, []);

  const handleSubmitDisposition = useCallback(
    async (dispositionPayload, { validateBeforeSubmit } = {}) => {
      if (validateBeforeSubmit) {
        const validationMessage = validateBeforeSubmit();
        if (validationMessage) {
          alert(validationMessage);
          return;
        }
      }

      setSubmittingDisposition(true);
      setSubmitDispositionFailed(false);

      try {
        await submitDisposition(rawId, dispositionPayload, faxProgramCode);
        setSubmittedDisposition(true);

        if (isSwitchProgram) {
          notifySwitchDispositionSuccess();
        } else {
          showNotification({
            title: "Successfully updated disposition!",
            message: "The disposition has been updated for this response.",
            position: "tr",
            type: "success",
          });
        }
      } catch {
        setSubmitDispositionFailed(true);

        if (isSwitchProgram) {
          notifySwitchDispositionError("tr");
          if (isSubmissionRequestFromSwim) {
            notifySwitchDispositionError("bl");
          }
        } else {
          showNotification({
            title: "Error!",
            message: "Unable to save disposition.",
            position: "tr",
            type: "error",
          });
        }
      } finally {
        setSubmittingDisposition(false);
      }
    },
    [rawId, isSwitchProgram, isSubmissionRequestFromSwim]
  );

  const handleSubmitPushToPharmacy = useCallback(
    async (dispositionPayload) => {
      setSubmittingDisposition(true);
      setSubmitDispositionFailed(false);

      try {
        await submitDisposition(rawId, dispositionPayload, faxProgramCode);
        setSubmittedDisposition(true);

        if (isSwitchProgram) {
          notifySwitchDispositionSuccess();
        }
      } catch {
        setSubmitDispositionFailed(true);

        if (isSwitchProgram) {
          notifySwitchDispositionError("tr");
          if (isSubmissionRequestFromSwim) {
            notifySwitchDispositionError("bl");
          }
        }
      } finally {
        setSubmittingDisposition(false);
      }
    },
    [rawId, isSwitchProgram, isSubmissionRequestFromSwim]
  );

  const handleSubmitForReview = useCallback(
    async (submitForReviewPayload) => {
      try {
        await submitRawForReview(rawId, submitForReviewPayload);

        if (contextIsRaw) {
          showNotification({
            title: "Submitted for review!",
            message: submitForReviewPayload.submitToPaw
              ? "The response has been submitted to PAW for review."
              : "The response has been submitted for review. The selected reviewer can now claim and process it.",
            position: "tr",
            type: "success",
          });
        }

        setEventRequestCompleted(true);
      } catch {
        showNotification({
          title: "Error!",
          message: "Unable to submit this response for review.",
          position: "tr",
          type: "error",
        });
      }
    },
    [rawId, contextIsRaw]
  );

  const handleDiscardRaw = useCallback(async () => {
    try {
      await discardRaw(rawId);

      if (contextIsRaw) {
        showNotification({
          title: "Response discarded!",
          message: "The fax response has been discarded and removed from the RAW list.",
          position: "tr",
          type: "success",
        });
      }

      setEventRequestCompleted(true);
    } catch {
      showNotification({
        title: "Error!",
        message: "Unable to discard this response.",
        position: "tr",
        type: "error",
      });
    }
  }, [rawId, contextIsRaw]);

  const handleUnclaimRaw = useCallback(async () => {
    try {
      await unclaimRaw(rawId);

      if (contextIsRaw) {
        showNotification({
          title: "Successfully unclaimed!",
          message:
            "The fax response has been unclaimed and is available for another user to claim.",
          position: "tr",
          type: "success",
        });
      }

      setEventRequestCompleted(true);
    } catch {
      showNotification({
        title: "Error!",
        message: "Unable to perform UNCLAIM on this response.",
        position: "tr",
        type: "error",
      });
    }
  }, [rawId, contextIsRaw]);

  useEffect(() => {
    if (!submittedDisposition && !submitDispositionFailed) {
      return;
    }

    setIsSubmissionRequestFromAw(true);
  }, [submittedDisposition, submitDispositionFailed]);

  useEffect(() => {
    if (!submittedDisposition && !eventRequestCompleted) {
      return;
    }

    const wfItemId = rawDetails?.wfItemId;

    if (isSubmissionRequestFromSwim && wfItemId) {
      const notificationType = submittedDisposition
        ? SWITCH_DETAILS_NOTIFICATION_TYPES.DISPOSITION_SUBMITTED
        : SWITCH_DETAILS_NOTIFICATION_TYPES.EVENT_EXECUTED;

      navigateToSwitchDetails(wfItemId, notificationType);
      return;
    }

    if (!contextIsRaw && !isSubmissionRequestFromAw) {
      if (wfItemId) {
        const notificationType = submittedDisposition
          ? SWITCH_DETAILS_NOTIFICATION_TYPES.DISPOSITION_SUBMITTED
          : SWITCH_DETAILS_NOTIFICATION_TYPES.EVENT_EXECUTED;

        navigateToSwitchDetails(wfItemId, notificationType);
      } else {
        navigate(-1);
      }
    }
  }, [
    submittedDisposition,
    eventRequestCompleted,
    isSubmissionRequestFromSwim,
    contextIsRaw,
    isSubmissionRequestFromAw,
    rawDetails?.wfItemId,
    navigateToSwitchDetails,
    navigate,
  ]);

  const shouldExit = submittedDisposition || eventRequestCompleted;

  return {
    rawDetails,
    rawDetailsStatus,
    formValues,
    setFieldValue,
    resetDispositionFields,
    submittingDisposition,
    submittedDisposition,
    submitDispositionFailed,
    eventRequestCompleted,
    shouldExit,
    handleSubmitDisposition,
    handleSubmitPushToPharmacy,
    handleSubmitForReview,
    handleUnclaimRaw,
    handleDiscardRaw,
    reloadRawDetails: loadRawDetails,
  };
};
